import { vsComponentName as masterAgreementDetailComponentName } from "./components/types/gamingContractMasterAgreement/detail";
import { vsComponentName as gamingContractDetailComponentName } from "./components/types/gamingContract/detail";
import { vsObjectName as gamingContractName } from "./configs/gamingContract";
import { vsObjectName as gamingContractMasterAgreementName } from "./configs/gamingContractMasterAgreement";
import {
  ContractService,
  ContractServiceProvider,
  vsProviderName as contractServiceProviderName,
  IContractTab,
} from "../../../Contract/shared/ContractService";

import { vsObjectName as machineName } from "../configs/machine";
import {
  MachineDetailTabsProvider,
  vsProviderName as machineDetailTabsProviderName,
} from "../../shared/components/views/machines/detail/tabs";
import {
  vsObjectName as gamingContractMachineName,
  vsMachineColumnSetName as machineColumnSetName,
  vsContractColumnSetName as contractColumnSetName,
} from "./configs/gamingContractMachine";
import {
  addContractTabToMachineDetail,
  getContractMachineTab,
} from "../../../Contract/shared/configs/contractMachines";
import { infoState } from "../../../Contract/shared/ContractService";
import { Contract } from "../../../Contract/shared/model/contract";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { ServiceResponse } from "../../../records/server/query";
import {
  vsComponentName as RecordFormViewComponentName,
  FormViewService,
  vsServiceName as FormViewServiceName,
  FormViewConfig,
} from "../../../records/forms/formView";
import { vsObjectName as masterAgreementAmendmentName } from "./configs/gamingContractMasterAgreementAmendment";
import { StateParams, StateService } from "@uirouter/angularjs";
import { Record } from "../../../records/server/crud";
import { vsComponentName as contractAmendmentsComponentName } from "./components/types/gamingContractMasterAgreement/amendments/component";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../../records/changeJournal/changeNoteModalService";
import { ChangeJournalObject } from "../../../records/changeJournal/changeJournalViewComponent/models";

const sensitiveInfoPermission = "contractSensitiveInfo";

registerContracts.$inject = [contractServiceProviderName];
export function registerContracts(
  contractServiceProvider: ContractServiceProvider,
) {
  contractServiceProvider.addContractType({
    id: gamingContractName,
    name: "Gaming Contract",
    detailComponentName: gamingContractDetailComponentName,
    formConfigName: gamingContractName,
    gridConfigName: gamingContractName,
    objectName: "GamingContract",
    sensitiveInfoPermission,
    tabs: [
      getContractMachineTab({
        contractTypeId: gamingContractName,
        machineColumnSetName: machineColumnSetName,
        machineName: machineName,
        gridViewSymbolKey: "gamingContractMachines",
        gridEditSymbolKey: "gamingContractMachines.edit",
        contractMachineObjectName: gamingContractMachineName,
      }),
    ],
    prepareClone(model) {
      return model;
    },
  });

  const amendmentBaseState = `${infoState}.amendments`;

  contractServiceProvider.addContractType({
    id: gamingContractMasterAgreementName,
    name: "Master Agreement",
    detailComponentName: masterAgreementDetailComponentName,
    formConfigName: gamingContractMasterAgreementName,
    gridConfigName: gamingContractMasterAgreementName,
    objectName: "GamingContractMasterAgreement",
    sensitiveInfoPermission,
    resolveAdditionalChangeJournalObjects: [
      "contract",
      ServerUtilityName,
      (contract: Contract, serverUtility: ServerUtility) =>
        getContractAmendments(serverUtility, contract).then((amendments) =>
          amendments.map(
            (a) =>
              <ChangeJournalObject>{
                id: {
                  ObjectId: a.Id,
                  ObjectName: masterAgreementAmendmentName,
                },
                object: a,
              },
          ),
        ),
    ],
    tabs: [
      <IContractTab>{
        name: "Amendments",
        uiSref: "amendments",
        state: {
          name: amendmentBaseState,
          url: "/amendments?amendmentId",
          params: { amendmentId: undefined },
          views: {
            tabs: {
              component: contractAmendmentsComponentName,
            },
          },
          resolve: {
            formViewConfig: [
              FormViewServiceName,
              (formViewService: FormViewService) =>
                formViewService.getFormViewConfig(masterAgreementAmendmentName),
            ],
            amendments: [
              "contract",
              ServerUtilityName,
              (contract: Contract, serverUtility: ServerUtility) =>
                getContractAmendments(serverUtility, contract),
            ],
            amendmentId: [
              "$stateParams",
              ($stateParams: StateParams) => {
                let num = Number.parseInt($stateParams["amendmentId"]);
                return Number.isNaN(num) ? undefined : num;
              },
            ],
            returnState: () => `${infoState}.amendment`,
          },
        },
        children: [
          {
            name: "add",
            uiSref: "add",
            state: {
              name: `${amendmentBaseState}.add`,
              url: "/add",
              views: {
                "tabs@main.contract.type.info": {
                  component: RecordFormViewComponentName,
                },
              },
              resolve: {
                fields: [
                  "formViewConfig",
                  (config: FormViewConfig) => config.fields,
                ],
                model: [
                  "contract",
                  (contract: Contract) => ({
                    GamingContractMasterAgreementId: contract.Id,
                  }),
                ],
                title: [() => "Add Amendment"],
                onSubmit: [
                  "$state",
                  ServerUtilityName,
                  ($state: StateService, serverUtility: ServerUtility) => [
                    "model",
                    (model: any) => {
                      return serverUtility
                        .sendApiRequest({
                          url: `/api/contract/masterAgreement/${model.GamingContractMasterAgreementId}/amendments`,
                          method: "post",
                          data: model,
                        })
                        .then(() => {
                          $state.go("^", undefined, { reload: true });
                        });
                    },
                  ],
                ],
                onCancel: [
                  "$state",
                  ($state: StateService) => () => $state.go("^"),
                ],
                showButtonsAtTop: [() => false],
              },
            },
          },
          {
            name: "edit",
            uiSref: "edit",
            state: {
              name: `${amendmentBaseState}.edit`,
              url: "/:amendmentId/edit",
              views: {
                "tabs@main.contract.type.info": {
                  component: RecordFormViewComponentName,
                },
              },
              resolve: {
                fields: [
                  "formViewConfig",
                  (config: FormViewConfig) => config.fields,
                ],
                model: [
                  "amendments",
                  "$stateParams",
                  (amendments: Record[], $stateParams: StateParams) =>
                    amendments.find(
                      (a) => a.Id === +$stateParams["amendmentId"],
                    ),
                ],
                title: [() => "Edit Amendment"],
                onSubmit: [
                  "$state",
                  ServerUtilityName,
                  ChangeJournalModalServiceName,
                  (
                      $state: StateService,
                      serverUtility: ServerUtility,
                      changeJournalModalService: ChangeJournalModalService,
                    ) =>
                    (model: any) =>
                      changeJournalModalService
                        .getAndSetChangeJournalNote(model)
                        .then(() =>
                          serverUtility.sendApiRequest({
                            url: `/api/contract/masterAgreement/amendments/${model.Id}`,
                            method: "put",
                            data: model,
                          }),
                        )
                        .then(() => {
                          $state.go("^", undefined, { reload: true });
                        }),
                ],
                onCancel: [
                  "$state",
                  ($state: StateService) => () => $state.go("^"),
                ],
                showButtonsAtTop: [() => false],
              },
            },
          },
        ],
      },
    ],
  });
}

function getContractAmendments(
  serverUtility: ServerUtility,
  contract: Contract,
) {
  return serverUtility
    .sendApiRequest<ServiceResponse<any[]>>({
      url: `/api/contract/masterAgreement/${contract.Id}/amendments`,
      method: "get",
    })
    .then((response) => response.Result);
}

registerMachineDetailGamingContractTab.$inject = [
  machineDetailTabsProviderName,
];
export function registerMachineDetailGamingContractTab(
  machineDetailsTabsProvider: MachineDetailTabsProvider,
) {
  addContractTabToMachineDetail({
    machineDetailsTabsProvider,
    displayName: "Gaming Contracts",
    id: "gamingContracts",
    uiSrefFragment: "gamingContract",
    contractTypeId: gamingContractName,
    gridViewSymbolKey: "machineGamingContracts",
    gridEditSymbolKey: "machines.gamingContract.edit",
    contractColumnSetName: contractColumnSetName,
    contractMachineName: gamingContractMachineName,
  });
}
